import { useEffect, useRef } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import clsx from 'clsx'

import { BrandLockup } from '@components/brand'
import ThemeSelector from '@components/theme/ThemeSelector'
import Button from '@components/ui/Button'
import { useOverlayCoordinator } from '@contexts/OverlayCoordinatorContext'
import { useReleaseCampaign } from '@contexts/ReleaseCampaignContext'
import { useReleaseCampaignAnalytics } from '@hooks/useReleaseCampaignAnalytics'
import { RELEASE_CAMPAIGN_PLACEMENTS } from '@services/releaseCampaignAnalytics'
import { panelTransition, tabIndicatorTransition } from '@utils/animations'
import { OVERLAY_IDS } from '@utils/overlayIds'

const NAV_ITEMS = [
  { label: 'Platform', to: '/features' },
  { label: 'Insights', to: '/blog', matchPrefix: true },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Trust', to: '/security' }
]

function Header() {
  const { activeOverlayId, closeOverlay, openOverlay } = useOverlayCoordinator()
  const isMenuOpen = activeOverlayId === OVERLAY_IDS.MOBILE_NAVIGATION
  const { campaign, isResolved = true } = useReleaseCampaign()
  const isCampaignActive = isResolved && campaign.enabled
  const { trackCtaClick } = useReleaseCampaignAnalytics(campaign)
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  const headerRef = useRef(null)
  const brandLinkRef = useRef(null)
  const desktopNavTargetRef = useRef(null)
  const menuButtonRef = useRef(null)
  const menuPanelRef = useRef(null)

  const isLoginPage = location.pathname === '/login'
  const isSignupPage = location.pathname === '/signup'
  const isDev = import.meta.env.DEV || window.location.hostname === 'localhost'
  const docsUrl = isDev ? 'http://localhost:3002/docs/' : '/docs/'
  const primaryCtaPath = isCampaignActive
    ? campaign.primaryCta.path || '/signup'
    : '/signup'
  const primaryCtaLabel = isCampaignActive
    ? campaign.primaryCta.label || 'Start with paper trading'
    : 'Start with paper trading'
  const handlePrimaryCtaClick = () => {
    trackCtaClick(RELEASE_CAMPAIGN_PLACEMENTS.PUBLIC_HEADER, primaryCtaPath)
  }
  const toggleMobileNavigation = () => {
    if (isMenuOpen) {
      closeOverlay(OVERLAY_IDS.MOBILE_NAVIGATION)
    } else {
      openOverlay(OVERLAY_IDS.MOBILE_NAVIGATION)
    }
  }

  const isActive = item =>
    item.matchPrefix
      ? location.pathname.startsWith(item.to)
      : location.pathname === item.to

  useEffect(() => {
    closeOverlay(OVERLAY_IDS.MOBILE_NAVIGATION)
  }, [location.pathname, closeOverlay])

  useEffect(
    () => () => closeOverlay(OVERLAY_IDS.MOBILE_NAVIGATION),
    [closeOverlay]
  )

  useEffect(() => {
    if (!isMenuOpen) return undefined

    const desktopMediaQuery = window.matchMedia('(min-width: 1024px)')
    const focusDesktopNavigation = () => {
      closeOverlay(OVERLAY_IDS.MOBILE_NAVIGATION)
      desktopNavTargetRef.current?.focus()
    }

    if (desktopMediaQuery.matches) {
      focusDesktopNavigation()
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    const backgroundRegions = Array.from(
      document.querySelectorAll('main, footer, [data-release-status]')
    ).filter(region => !headerRef.current?.contains(region))
    const backgroundRegionState = backgroundRegions.map(region => ({
      region,
      hadInert: region.hasAttribute('inert'),
      inertValue: region.getAttribute('inert'),
      ariaHidden: region.getAttribute('aria-hidden')
    }))

    document.body.style.overflow = 'hidden'
    backgroundRegions.forEach(region => {
      region.setAttribute('inert', '')
      region.setAttribute('aria-hidden', 'true')
    })

    const focusableSelector = 'a[href], button:not([disabled])'
    const panelFocusable = Array.from(
      menuPanelRef.current?.querySelectorAll(focusableSelector) || []
    )
    const focusable = [
      brandLinkRef.current,
      menuButtonRef.current,
      ...panelFocusable
    ].filter(Boolean)

    panelFocusable[0]?.focus()

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        closeOverlay(OVERLAY_IDS.MOBILE_NAVIGATION)
        menuButtonRef.current?.focus()
        return
      }

      if (event.key !== 'Tab' || focusable.length === 0) return

      const currentIndex = focusable.indexOf(document.activeElement)
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex < 0 || currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1

      event.preventDefault()
      focusable[nextIndex].focus()
    }

    const handleDesktopChange = event => {
      if (event.matches) focusDesktopNavigation()
    }

    document.addEventListener('keydown', handleKeyDown)
    desktopMediaQuery.addEventListener('change', handleDesktopChange)
    return () => {
      document.body.style.overflow = previousOverflow
      backgroundRegionState.forEach(
        ({ region, hadInert, inertValue, ariaHidden }) => {
          if (hadInert) {
            region.setAttribute('inert', inertValue ?? '')
          } else {
            region.removeAttribute('inert')
          }

          if (ariaHidden === null) {
            region.removeAttribute('aria-hidden')
          } else {
            region.setAttribute('aria-hidden', ariaHidden)
          }
        }
      )
      document.removeEventListener('keydown', handleKeyDown)
      desktopMediaQuery.removeEventListener('change', handleDesktopChange)
    }
  }, [isMenuOpen, closeOverlay])

  const navLinkClasses = item =>
    clsx(
      'relative flex min-h-[44px] items-center text-sm font-medium tracking-normal transition-colors',
      isActive(item)
        ? 'text-[var(--x3-control-accent)]'
        : 'text-[var(--x3-text-muted)] hover:text-[var(--x3-text)]'
    )

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 border-b border-[var(--x3-border)] bg-[var(--x3-header)] print:hidden"
      role={isMenuOpen ? 'dialog' : undefined}
      aria-modal={isMenuOpen ? 'true' : undefined}
      aria-label={isMenuOpen ? 'Navigation menu' : undefined}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          ref={brandLinkRef}
          to="/"
          aria-label="x3Algo home"
          className="shrink-0"
        >
          <BrandLockup size="md" />
        </Link>

        <nav
          className="hidden items-center gap-7 lg:flex"
          aria-label="Primary navigation"
        >
          {NAV_ITEMS.map((item, index) => (
            <Link
              key={item.to}
              ref={index === 0 ? desktopNavTargetRef : undefined}
              to={item.to}
              className={navLinkClasses(item)}
              aria-current={isActive(item) ? 'page' : undefined}
            >
              {item.label}
              {isActive(item) && (
                <motion.span
                  layoutId="public-navigation-indicator"
                  className="absolute inset-x-0 bottom-0 h-0.5 bg-[#4F46E5]"
                  transition={
                    reduceMotion ? { duration: 0 } : tabIndicatorTransition
                  }
                />
              )}
            </Link>
          ))}
          <a
            href={docsUrl}
            className="flex min-h-[44px] items-center text-sm font-medium text-[var(--x3-text-muted)] hover:text-[var(--x3-text)]"
            target={isDev ? '_blank' : undefined}
            rel={isDev ? 'noopener noreferrer' : undefined}
          >
            Docs
          </a>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <ThemeSelector />
          {!isLoginPage && (
            <Link
              to="/login"
              className="inline-flex min-h-[44px] items-center px-3 text-sm font-semibold text-[var(--x3-text)] hover:text-[var(--x3-control-accent)]"
            >
              Log in
            </Link>
          )}
          {!isSignupPage && (
            <Button
              as={Link}
              to={primaryCtaPath}
              variant="primary"
              size="md"
              onClick={handlePrimaryCtaClick}
            >
              {primaryCtaLabel}
            </Button>
          )}
        </div>

        <Button
          ref={menuButtonRef}
          type="button"
          variant="ghost"
          size="icon"
          onClick={toggleMobileNavigation}
          className="text-[var(--x3-text)] lg:hidden"
          aria-label={
            isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'
          }
          aria-expanded={isMenuOpen}
          aria-controls="public-mobile-navigation"
          title={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <motion.span
            animate={reduceMotion ? undefined : { rotate: isMenuOpen ? 90 : 0 }}
            transition={reduceMotion ? { duration: 0 } : panelTransition}
          >
            {isMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </motion.span>
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {isMenuOpen && (
          <motion.div
            id="public-mobile-navigation"
            className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto border-t border-[var(--x3-border)] bg-[var(--x3-header)] px-4 py-6 shadow-lg lg:hidden"
            initial={reduceMotion ? false : { y: -12 }}
            animate={{ y: 0 }}
            exit={reduceMotion ? undefined : { y: -12 }}
            transition={reduceMotion ? { duration: 0 } : panelTransition}
          >
            <div ref={menuPanelRef} className="h-full">
              <nav
                className="mx-auto flex max-w-lg flex-col"
                aria-label="Mobile navigation"
              >
                {NAV_ITEMS.map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={clsx(
                      'flex min-h-[52px] items-center border-b border-[var(--x3-border)] text-base font-semibold',
                      isActive(item)
                        ? 'text-[var(--x3-control-accent)]'
                        : 'text-[var(--x3-text)]'
                    )}
                    aria-current={isActive(item) ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                ))}
                <a
                  href={docsUrl}
                  className="flex min-h-[52px] items-center border-b border-[var(--x3-border)] text-base font-semibold text-[var(--x3-text)]"
                  target={isDev ? '_blank' : undefined}
                  rel={isDev ? 'noopener noreferrer' : undefined}
                >
                  Docs
                </a>
                <ThemeSelector className="mt-6 w-full" showLabels />
                <div className="mt-8 grid gap-3">
                  {!isLoginPage && (
                    <Button
                      as={Link}
                      to="/login"
                      variant="secondary"
                      size="lg"
                      fullWidth
                    >
                      Log in
                    </Button>
                  )}
                  {!isSignupPage && (
                    <Button
                      as={Link}
                      to={primaryCtaPath}
                      variant="primary"
                      size="lg"
                      fullWidth
                      onClick={handlePrimaryCtaClick}
                    >
                      {primaryCtaLabel}
                    </Button>
                  )}
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header
